// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import updateBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/UpdateBillTo";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import CustomerAddressForm from "@insite/content-library/Components/CustomerAddressForm";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Loader from "@store/Library/Components/Toolkit/Loader";
import { AddressInfoDisplayStyles } from "@store/Overrides/Components/AddressInfoDisplay";
import { addressProps } from "@store/Themes/common/props/address";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import { useIsB2BCustomer } from "@store/Framework/Store/Data/BillTos/BillTosHooks";
/* Store Custom Code - End */

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    currentBillTo: getCurrentBillToState(state).value,
    countries: getCurrentCountries(state),
    billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
});

const mapDispatchToProps = {
    updateBillTo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CurrentBillingAddressStyles {
    gridContainer?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyProps;
    editLinkGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    addressFormModal?: ModalPresentationProps;
    addressInfoDisplayGridItem?: GridItemProps;
    // Starter Custom Code
    wrapper?: InjectableCss;
    addressInfoDisplay?: AddressInfoDisplayStyles;
}

export const currentBillingAddressStyles: CurrentBillingAddressStyles = {
    wrapper: addressProps.box,
    headingText: addressProps.title,
    addressInfoDisplay: {
        wrapper: addressProps.wrapper,
    },
    editLink: addressProps.linkEdit,
    addressFormModal: { sizeVariant: "medium" },
};

const styles = currentBillingAddressStyles;

const CurrentBillingAddress: React.FunctionComponent<Props> = (props: Props) => {
    const isB2BCustomer = useIsB2BCustomer(); // Store Custom Code

    const { currentBillTo } = props;

    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    const trackBillingInfoChanged = ga.use.trackBillingInfoChanged(); // Starter Custom Code
    /* Starter Removed - return null */

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const editClickHandler = () => {
        setModalIsOpen(true);
    };
    const formCancelHandler = (e: any) => {
        e.preventDefault();
        modalCloseHandler();
    };
    const formSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: BillToModel) => {
        if (!props.currentBillTo) {
            return;
        }
        props.updateBillTo({
            billTo: {
                ...props.currentBillTo,
                ...address,
            },
            isEdit: true,
        });
        trackBillingInfoChanged(); // Starter Custom Code
        modalCloseHandler();
    };

    const { countries, billToAddressFields } = props;

    /* Starter Custom Code - Start */
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography /* Starter Removed - as */ {...styles.headingText}>
                {translate("Billing Information")}
            </Typography>
            {!currentBillTo ? (
                <Loader />
            ) : (
                <>
                    <AddressInfoDisplay
                        firstName={currentBillTo.firstName}
                        lastName={currentBillTo.lastName}
                        companyName={currentBillTo.companyName}
                        attention={currentBillTo.attention}
                        address1={currentBillTo.address1}
                        address2={currentBillTo.address2}
                        address3={currentBillTo.address3}
                        address4={currentBillTo.address4}
                        city={currentBillTo.city}
                        state={currentBillTo.state ? currentBillTo.state.abbreviation : undefined}
                        postalCode={currentBillTo.postalCode}
                        country={currentBillTo.country ? currentBillTo.country.abbreviation : undefined}
                        phone={currentBillTo.phone}
                        fax={currentBillTo.fax}
                        email={currentBillTo.email}
                        extendedStyles={styles.addressInfoDisplay}
                    />
                    {countries && /* Store Custom Code */ !isB2BCustomer && (
                        <Link {...styles.editLink} onClick={editClickHandler} data-test-selector="billTo_Edit_Address">
                            {translate("Edit")}
                            <VisuallyHidden>{translate("Billing Information")}</VisuallyHidden>
                        </Link>
                    )}
                    {countries && billToAddressFields && (
                        <Modal
                            headline={translate("Edit Billing Information")}
                            {...styles.addressFormModal}
                            isOpen={modalIsOpen}
                            handleClose={modalCloseHandler}
                        >
                            <CustomerAddressForm
                                address={currentBillTo}
                                countries={countries}
                                addressFieldDisplayCollection={billToAddressFields}
                                onCancel={formCancelHandler}
                                onSubmit={formSubmitHandler}
                            />
                        </Modal>
                    )}
                </>
            )}
        </StyledWrapper>
    );
    /* Starter Custom Code - End */
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CurrentBillingAddress),
    definition: {
        group: "Addresses",
        allowedContexts: ["AddressesPage"],
    },
};

export default widgetModule;
